/**
 * voice-startup-timing — the click → first spoken word critical path, measured.
 *
 * Prod forensics (2026-09-12, session ba40c0f7) put ~9.7s between a visitor
 * clicking the launcher and the greeting appearing, and the only numbers we had
 * came from the BOT's log: everything before `client-ready` was a black box we
 * could only infer from the gap between the server seeing the transport connect
 * and the client announcing itself (~2s, unexplained). This module closes that
 * hole from the browser side, where the missing segments actually live:
 *
 *   click
 *     → POST /sites/web-agent/session      (conversation open/resume)
 *     → POST /sites/start-session          (spawn; the two are SERIALIZED by a
 *                                           data dependency on conversationId)
 *     → Daily call-machine bundle loaded   (~1MB, historically fetched AFTER
 *                                           the click — see startup-prefetch.ts)
 *     → Daily join resolved
 *     → protocol bot participant seen
 *     → Daily reports a connected message path (already true when the visitor
 *                                           joined first; FLO-1140)
 *     → client-ready sent                  (the boundary the bot log starts at)
 *     → bot-ready received
 *     → first bot speech / caption
 *
 * Two rules this module lives by:
 *
 * 1. **Monotonic only.** Every mark is `performance.now()`, never `Date.now()`:
 *    a wall-clock step (NTP, a laptop waking) mid-session would otherwise
 *    produce negative "durations" that are indistinguishable from a real win.
 * 2. **Never on the critical path it measures.** Everything here is wrapped so
 *    a missing `performance`, a blocked PerformanceObserver, or a thrown emit
 *    costs the visitor nothing. Timing that can break the thing it times is
 *    worse than no timing.
 *
 * The network marks come from a `PerformanceObserver` on `resource` entries
 * rather than from wrapping `fetch`: `startBotAndConnect()` issues the
 * start-session POST inside pipecat's client, and daily-js appends the
 * call-machine `<script>` itself, so neither is a call site we own. Resource
 * timing sees both without patching anything global on the customer's page.
 *
 * One `VOICE_READY` visitor event carries the result (see visitor-events.ts and
 * floe-api's PROPS_SCHEMA — flat numbers only, no nested `marks` object: the
 * public ingest path rebuilds props from a primitive schema and would drop one).
 */

import { logger as defaultLogger, type Logger } from './logger';

/** Named points on the click → greeting path, in the order they occur. */
export type VoiceStartupMark =
  | 'conversationOpened'
  | 'startSessionResponse'
  | 'dailyBundleLoaded'
  | 'dailyJoined'
  | 'botParticipantSeen'
  | 'clientReadySent'
  | 'botReady'
  | 'firstBotSpeech';

/** Substring that identifies the Daily call-machine bundle in resource timing. */
const DAILY_BUNDLE_MATCH = 'call-machine-object-bundle';

/** Substring that identifies the spawn POST in resource timing. */
const START_SESSION_MATCH = '/sites/start-session';

/**
 * Ceiling on how long a timeline stays armed. A visitor who opens the widget
 * and never gets a greeting must not leave an observer running for the life of
 * the page, and a `firstBotSpeech` mark that lands minutes later is not a
 * startup measurement any more.
 */
export const VOICE_STARTUP_WINDOW_MS = 120_000;

function now(): number | null {
  try {
    return typeof performance !== 'undefined' &&
      typeof performance.now === 'function'
      ? performance.now()
      : null;
  } catch {
    return null;
  }
}

/**
 * One click → greeting measurement.
 *
 * A module-level singleton ({@link voiceStartupTimeline}) rather than an
 * instance field because the two lowest marks are taken inside
 * `FloeDailyTransport`, which the SDK constructs but does not thread itself
 * into. `start()` resets, so a second session simply replaces the first — a
 * second `Floe()` on one page would share it, which is the same trade the
 * visitor-token latch already makes and is preferable to a transport that has
 * to know which SDK instance built it.
 */
export class VoiceStartupTimeline {
  private t0: number | null = null;
  private readonly marks = new Map<VoiceStartupMark, number>();
  private observer: PerformanceObserver | null = null;
  private windowTimer: ReturnType<typeof setTimeout> | null = null;
  private prefetched = false;
  private emitted = false;
  private onComplete: ((props: Record<string, number | boolean>) => void) | null =
    null;
  /**
   * Per-mark listeners (FLO-960 diagnostics). Separate from {@link onReady},
   * which is a single one-shot completion slot owned by visitor-events: a
   * second consumer must not have to fight for it or re-derive marks from the
   * aggregate. Listeners are told the mark AND its ms-from-click, because the
   * only other place that offset exists is `msTo()` after the fact.
   */
  private readonly markListeners = new Set<
    (name: VoiceStartupMark, msFromClick: number) => void
  >();

  constructor(private readonly logger: Logger = defaultLogger) {}

  /**
   * Arm a fresh measurement at the visitor's click.
   *
   * `prefetched` records whether startup-prefetch had already warmed the Daily
   * bundle — without it the emitted numbers can't distinguish "prefetch works"
   * from "this visitor happened to have a warm cache".
   */
  start(options?: { prefetched?: boolean }): void {
    this.stopObserver();
    this.marks.clear();
    this.emitted = false;
    this.prefetched = options?.prefetched === true;
    this.t0 = now();
    this.startObserver();
    this.armWindowTimer();
  }

  /** Register the sink for the one-shot completion event. */
  onReady(handler: (props: Record<string, number | boolean>) => void): void {
    this.onComplete = handler;
  }

  /**
   * Observe every mark as it lands. Returns an unsubscribe function.
   *
   * Deliberately NOT cleared by {@link start}: the timeline is a module
   * singleton that a transport also writes to, so a consumer that had to
   * re-register per session would miss any mark taken before it did. Each
   * notified mark already carries its own offset from the current t0, so a
   * listener needs no per-session state to attribute it.
   */
  onMark(handler: (name: VoiceStartupMark, msFromClick: number) => void): () => void {
    this.markListeners.add(handler);
    return () => this.markListeners.delete(handler);
  }

  /**
   * Record a mark. First write wins; later ones are ignored.
   *
   * `at` is an optional `performance.now()`-origin timestamp for callers that
   * know exactly when the thing happened — the resource observer passes the
   * entry's `responseEnd`, since its callback runs some time after the fetch
   * actually finished. Everything else lets the mark be stamped "now".
   */
  mark(name: VoiceStartupMark, at?: number): void {
    if (this.t0 === null || this.marks.has(name)) return;
    const when = typeof at === 'number' && Number.isFinite(at) ? at : now();
    if (when === null) return;
    if (when - this.t0 > VOICE_STARTUP_WINDOW_MS) {
      // Past the window this measures. Disarm rather than emit a number that
      // would read as a catastrophic regression in the aggregate.
      this.abandon();
      return;
    }
    const msFromClick = Math.max(0, when - this.t0);
    this.marks.set(name, msFromClick);
    this.notifyMark(name, msFromClick);
    if (name === 'firstBotSpeech') this.complete();
  }

  /**
   * Fan a mark out to observers. Each handler is isolated: a throwing listener
   * must never cost the measurement (or the greeting) it is observing.
   */
  private notifyMark(name: VoiceStartupMark, msFromClick: number): void {
    for (const handler of this.markListeners) {
      try {
        handler(name, msFromClick);
      } catch {
        /* observers are best-effort by construction */
      }
    }
  }

  /** Drop the measurement (session cancelled, superseded, or failed to start). */
  abandon(): void {
    this.clearWindowTimer();
    this.stopObserver();
    this.t0 = null;
    this.marks.clear();
  }

  /**
   * The window deadline, scheduled rather than merely checked: `mark()` only
   * runs when something happens, so a visitor whose session never produced
   * another event (bot never joined, tab left open on "Connecting…") would
   * otherwise keep the resource observer attached for the life of the page.
   */
  private armWindowTimer(): void {
    this.clearWindowTimer();
    try {
      this.windowTimer = setTimeout(() => {
        this.windowTimer = null;
        this.abandon();
      }, VOICE_STARTUP_WINDOW_MS);
    } catch {
      this.windowTimer = null;
    }
  }

  private clearWindowTimer(): void {
    if (this.windowTimer === null) return;
    try {
      clearTimeout(this.windowTimer);
    } catch {
      /* already fired or never scheduled */
    }
    this.windowTimer = null;
  }

  /** Milliseconds from click to `name`, or null when it never happened. */
  msTo(name: VoiceStartupMark): number | null {
    const value = this.marks.get(name);
    return value === undefined ? null : Math.round(value);
  }

  private complete(): void {
    if (this.emitted) return;
    this.emitted = true;
    this.clearWindowTimer();
    this.stopObserver();
    const props = this.props();
    try {
      this.logger.log(
        '[OnboardingSDK] ⏱️ voice startup (ms from click):',
        JSON.stringify(props),
      );
    } catch {
      /* logging must never cost the greeting */
    }
    try {
      this.onComplete?.(props);
    } catch (e) {
      try {
        this.logger.warn('[OnboardingSDK] voice-startup emit failed:', e);
      } catch {
        /* nothing left to do */
      }
    }
  }

  /**
   * The emitted shape: flat primitives, every key optional. A mark that never
   * landed is OMITTED rather than sent as 0 or -1 — a missing segment and a
   * zero-length one are different facts, and an aggregate that cannot tell them
   * apart is worse than a gap.
   */
  private props(): Record<string, number | boolean> {
    const out: Record<string, number | boolean> = { prefetched: this.prefetched };
    const put = (key: string, mark: VoiceStartupMark) => {
      const value = this.msTo(mark);
      if (value !== null) out[key] = value;
    };
    put('msSinceClick', 'firstBotSpeech');
    put('msToConversation', 'conversationOpened');
    put('msToStartSession', 'startSessionResponse');
    put('msToDailyBundle', 'dailyBundleLoaded');
    put('msToDailyJoined', 'dailyJoined');
    put('msToBotParticipant', 'botParticipantSeen');
    put('msToClientReady', 'clientReadySent');
    put('msToBotReady', 'botReady');
    return out;
  }

  /**
   * Watch resource timing for the two network legs nothing in this package
   * issues itself. Best-effort: an environment without PerformanceObserver
   * (jsdom without the polyfill, an old Safari) simply omits those two marks.
   */
  private startObserver(): void {
    try {
      if (typeof PerformanceObserver !== 'function') return;
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // `buffered: true` replays entries recorded BEFORE this measurement
          // was armed — and a successful prefetch is exactly such an entry: the
          // bundle is fetched at page load or on hover, seconds before the
          // click. Crediting it would stamp `msToDailyBundle` with the moment
          // this callback happened to run (65ms on the first local end-to-end
          // run), which reads as a real, absurdly fast fetch. The contract is
          // the opposite: a segment that did not happen inside the window is
          // OMITTED, and that absence is what tells the aggregate the prefetch
          // worked (see the VOICE_READY block in floe-api's PROPS_SCHEMA).
          //
          // The cut is made on `responseEnd`, not `startTime`: what the greeting
          // waits for is the fetch FINISHING. A prefetch that was still in
          // flight at the click (hovered, clicked 200ms later, bundle landed
          // 900ms after that) is a real segment of this startup and must be
          // reported — as `responseEnd - t0`, the moment the bytes arrived, not
          // the moment this callback ran. One that completed before the click
          // is not on the path at all. Both timestamps share
          // `performance.now()`'s origin, so they compare directly with t0.
          // `responseEnd` is always exposed for cross-origin resources (only
          // the intermediate phases need Timing-Allow-Origin); `startTime` is
          // the fallback for an entry that somehow lacks it.
          const { responseEnd } = entry as PerformanceResourceTiming;
          const finishedAt =
            typeof responseEnd === 'number' && responseEnd > 0
              ? responseEnd
              : entry.startTime;
          if (this.t0 !== null && finishedAt < this.t0) continue;
          if (entry.name.includes(DAILY_BUNDLE_MATCH)) {
            this.mark('dailyBundleLoaded', finishedAt);
          } else if (entry.name.includes(START_SESSION_MATCH)) {
            this.mark('startSessionResponse', finishedAt);
          }
        }
      });
      this.observer.observe({ type: 'resource', buffered: true });
    } catch {
      this.observer = null;
    }
  }

  private stopObserver(): void {
    try {
      this.observer?.disconnect();
    } catch {
      /* already gone */
    }
    this.observer = null;
  }
}

/** The one timeline the SDK and its Daily transport both write to. */
export const voiceStartupTimeline = new VoiceStartupTimeline();
